import { api } from './client'
import type {
  LeadRoutingMember,
  LeadRoutingMemberBody,
  LeadRoutingRule,
  LeadRoutingRuleRequestBody,
} from './leadRoutingRules'

/**
 * F-116 (REQ-070-002, REQ-071-002): instradamento per territorio e condizioni guidate.
 * Aggiunge al modulo `leadRoutingRules.ts` il vocabolario delle condizioni (`GET api/lead-routing-rules/vocabulary`),
 * l'area geografica e la soglia di performance per membro, e l'opzione "salta se l'agente non e' disponibile".
 */

export interface LeadRoutingOption {
  value: string
  label: string
}

export interface LeadRoutingField {
  key: string
  label: string
  /** `enum`, `text`, `number`, `province` o `region`. */
  type: string
  operators: string[]
  /** Valori proposti (fonte, tipo, province, regioni, ore, giorni); assenti per i campi liberi. */
  options?: LeadRoutingOption[] | null
}

export interface LeadRoutingProvince {
  code: string
  name: string
  region: string
}

export interface LeadRoutingVocabulary {
  fields: LeadRoutingField[]
  operators: { key: string; label: string }[]
  provinces: LeadRoutingProvince[]
  regions: string[]
}

export interface LeadRoutingMemberExt extends LeadRoutingMember {
  /** Province ammesse (sigle); vuoto = tutte. */
  areaProvinces?: string[] | null
  /** Regioni ammesse; vuoto = tutte. */
  areaRegions?: string[] | null
  /** 0..1; null = ignorata. Convertiti su assegnati negli ultimi 30 giorni. */
  minConversionRate?: number | null
}

export interface LeadRoutingMemberBodyExt extends LeadRoutingMemberBody {
  areaProvinces?: string[] | null
  areaRegions?: string[] | null
  minConversionRate?: number | null
}

export interface LeadRoutingRuleExt extends Omit<LeadRoutingRule, 'members'> {
  members: LeadRoutingMemberExt[]
  skipWhenTargetUnavailable?: boolean
}

export interface LeadRoutingRuleBodyExt extends Omit<LeadRoutingRuleRequestBody, 'members'> {
  members?: LeadRoutingMemberBodyExt[]
  /** Omesso = invariato. */
  skipWhenTargetUnavailable?: boolean
}

export const leadRoutingApi = {
  vocabulary: () => api<LeadRoutingVocabulary>('/api/lead-routing-rules/vocabulary'),
  list: () => api<LeadRoutingRuleExt[]>('/api/lead-routing-rules'),
  create: (data: LeadRoutingRuleBodyExt) =>
    api<{ id: number }>('/api/lead-routing-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: LeadRoutingRuleBodyExt) =>
    api<void>(`/api/lead-routing-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
}

/** Una condizione di una regola: tutti e tre i membri sono testo. */
export interface RoutingCondition {
  field: string
  operator: string
  value: string
}

/** Legge le condizioni salvate (testo JSON); `null` se non sono un array di oggetti leggibili. */
export function parseConditions(raw: string): RoutingCondition[] | null {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    return parsed.map(c => {
      const o = (c ?? {}) as Record<string, unknown>
      return {
        field: typeof o['field'] === 'string' ? o['field'] : '',
        operator: typeof o['operator'] === 'string' ? o['operator'] : 'eq',
        value: typeof o['value'] === 'string' ? o['value'] : String(o['value'] ?? ''),
      }
    })
  } catch {
    return null
  }
}

/** Il testo JSON che il backend riceve: solo le condizioni compilate. */
export function serializeConditions(conditions: RoutingCondition[]): string {
  return JSON.stringify(
    conditions
      .filter(c => c.field !== '')
      .map(c => ({ field: c.field, operator: c.operator, value: c.value })),
  )
}

/** Gli elementi di un valore `in` (elenco separato da virgole). */
export function splitInValue(value: string): string[] {
  return value.split(',').map(x => x.trim()).filter(x => x.length > 0)
}
